import React, { useState } from 'react';
import { useUser } from './usersContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';

const subjectOptions = [
  "math", "physics", "chemistry", "biology", "french", "english", "spanish",
  "history", "philosophy", "coding", "music", "geography"
];

const MyInformation = () => {
  const { currentUser, userDoc, setUserDoc } = useUser();
  const [subjects, setSubjects] = useState(userDoc?.subjects || []);
  const [saving, setSaving] = useState(false);
  const isTutor = userDoc?.userType === "tutor" || userDoc?.userType === "Tutor";
  const statusMessage = useStatusMessage();
  const [editing, setEditing] = useState({
    name: false,
    phone: false
  });
  const [hoveredButton, setHoveredButton] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);// Initialize user info from userDoc or currentUser
  const [userInfo, setUserInfo] = useState({
    name: userDoc?.firstName && userDoc?.lastName 
      ? `${userDoc.firstName} ${userDoc.lastName}` 
      : userDoc?.name || currentUser?.displayName || "",
    phone: userDoc?.phone || ""
  });
  // Update userInfo when userDoc changes
  React.useEffect(() => {
    if (userDoc || currentUser) {
      setUserInfo({
        name: userDoc?.firstName && userDoc?.lastName 
          ? `${userDoc.firstName} ${userDoc.lastName}` 
          : userDoc?.name || currentUser?.displayName || "",
        phone: userDoc?.phone || ""
      });
    }
  }, [userDoc, currentUser]);  const styles = {
    infoContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem'
    },
    infoSection: {
      borderBottom: '1px solid #e1e4e8',
      padding: '2rem 0'
    },
    infoHeader: {
      fontSize: '20px',
      fontWeight: '500', // Changed from 500
      marginBottom: '1rem'
    },
    infoField: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '1rem 0',
      padding: '0.5rem',
      borderRadius: '6px'
    },
    fieldLabel: {
      fontSize: '14px',
      fontWeight: '500', // Changed from 500
      color: '#24292e',
      marginBottom: '0.5rem'
    },
    fieldContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      width: '100%'
    },
    fieldValue: {
      flex: 1,
      fontSize: '14px',
      color: '#586069'
    },
    editButton: {
      background: 'none',
      border: 'none',
      color: '#586069',
      cursor: 'pointer',
      padding: '0.5rem'
    },
    editInput: {
      width: '100%',
      padding: '0.5rem',
      border: '1px solid #e1e4e8',
      borderRadius: '6px',
      fontSize: '14px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem'
    },
    saveButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
      backgroundColor: 'rgb(30, 178, 252)',
      color: 'white',
      border: 'none',
      transition: 'background-color 0.2s ease'
    },
    saveButtonDisabled: {
      backgroundColor: '#cccccc',
      cursor: 'not-allowed',
      opacity: '0.7'
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
      backgroundColor: '#fafbfc',
      border: '1px solid #e1e4e8'
    },
    cancelButtonDisabled: {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
      opacity: '0.7'
    }
  };
  const handleEdit = (field) => {
    setEditing({ ...editing, [field]: true });
  };

  const handleSave = async (field) => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      
      // Prepare update data based on field
      let updateData = {};
        if (field === 'name') {
        // Split name into firstName and lastName if possible
        const nameParts = userInfo.name.trim().split(' ');
        if (nameParts.length >= 2) {
          updateData.firstName = nameParts[0];
          updateData.lastName = nameParts.slice(1).join(' ');
        } else {
          updateData.firstName = nameParts[0] || '';
          updateData.lastName = '';
        }
        updateData.name = userInfo.name;
      } else if (field === 'phone') {
        updateData.phone = userInfo.phone;
      }
      
      // Update Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        // Update local userDoc state
      setUserDoc({ ...userDoc, ...updateData });
      
      setEditing({ ...editing, [field]: false });
      statusMessage.showSuccess(`${field === 'name' ? 'Name' : 'Phone number'} updated successfully`);
    } catch (error) {
      console.error('Error updating user info:', error);
      statusMessage.showError('Failed to update information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (field) => {    // Reset to original values
    if (field === 'name') {
      setUserInfo({
        ...userInfo,
        name: userDoc?.firstName && userDoc?.lastName 
          ? `${userDoc.firstName} ${userDoc.lastName}` 
          : userDoc?.name || currentUser?.displayName || ""
      });
    } else if (field === 'phone') {
      setUserInfo({
        ...userInfo,
        phone: userDoc?.phone || ""
      });
    }
    
    setEditing({ ...editing, [field]: false });
  };

  const handleSubjectChange = (subject) => {
    setSubjects((prev) =>
      prev.includes(subject)
        ? prev.filter((s) => s !== subject)
        : [...prev, subject]
    );
  };

  const handleSaveSubjects = async () => {
    if (!currentUser) return;
    setSaving(true);    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { subjects });
      setUserDoc({ ...userDoc, subjects });
      statusMessage.showSuccess('Subjects updated successfully');
    } catch (error) {
      statusMessage.showError('Failed to update subjects. Please try again.');
    }
    setSaving(false);
  };
  return (
    <div style={styles.infoContainer}>
      <div style={styles.infoSection}>
        <h2 style={styles.infoHeader}>Public Profile</h2>        <div 
          style={{
            ...styles.infoField,
            backgroundColor: hoveredField === 'name' ? '#f6f8fa' : 'transparent'
          }}
          onMouseEnter={() => setHoveredField('name')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <div style={styles.fieldContent}>
            <div>
              <div style={styles.fieldLabel}>Name</div>
              {editing.name ? (
                <>
                  <input 
                    style={styles.editInput}
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({...userInfo, name: e.target.value})}
                    disabled={saving}
                  />
                  <div style={styles.buttonGroup}>
                    <button 
                      style={{
                        ...styles.saveButton,
                        ...(saving ? styles.saveButtonDisabled : {})
                      }}
                      onClick={() => handleSave('name')}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      style={{
                        ...styles.cancelButton,
                        ...(saving ? styles.cancelButtonDisabled : {})
                      }}
                      onClick={() => handleCancel('name')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div style={styles.fieldValue}>{userInfo.name || 'No name set'}</div>
              )}
            </div>
            {!editing.name && (
              <button 
                style={{
                  ...styles.editButton,
                  color: hoveredButton === 'name' ? 'rgb(30, 178, 252)' : '#586069'
                }}
                onClick={() => handleEdit('name')}
                onMouseEnter={() => setHoveredButton('name')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <i className='bx bxs-pencil'></i>
              </button>
            )}
          </div>
        </div>

        <div style={styles.infoField}>
          <div style={styles.fieldContent}>
            <div>
              <div style={styles.fieldLabel}>Email</div>
              <div style={{ ...styles.fieldValue, color: '#666', fontStyle: 'italic' }}>
                {currentUser?.email || 'No email available'}
                <br />
                <small style={{ fontSize: '12px' }}>Email cannot be changed here. Contact support if needed.</small>
              </div>
            </div>
          </div>
        </div>        <div 
          style={{
            ...styles.infoField,
            backgroundColor: hoveredField === 'phone' ? '#f6f8fa' : 'transparent'
          }}
          onMouseEnter={() => setHoveredField('phone')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <div style={styles.fieldContent}>
            <div>
              <div style={styles.fieldLabel}>Phone</div>
              {editing.phone ? (
                <>
                  <input 
                    style={styles.editInput}
                    value={userInfo.phone}
                    onChange={(e) => setUserInfo({...userInfo, phone: e.target.value})}
                    disabled={saving}
                    placeholder="Enter your phone number"
                  />
                  <div style={styles.buttonGroup}>
                    <button 
                      style={{
                        ...styles.saveButton,
                        ...(saving ? styles.saveButtonDisabled : {})
                      }}
                      onClick={() => handleSave('phone')}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      style={{
                        ...styles.cancelButton,
                        ...(saving ? styles.cancelButtonDisabled : {})
                      }}
                      onClick={() => handleCancel('phone')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div style={styles.fieldValue}>{userInfo.phone || 'No phone number set'}</div>
              )}
            </div>
            {!editing.phone && (
              <button 
                style={{
                  ...styles.editButton,
                  color: hoveredButton === 'phone' ? 'rgb(30, 178, 252)' : '#586069'
                }}
                onClick={() => handleEdit('phone')}
                onMouseEnter={() => setHoveredButton('phone')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <i className='bx bxs-pencil'></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {isTutor && (
        <fieldset
          style={{
            margin: '1.5rem 0',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            background: '#f9f9f9',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.04)'
          }}
        >
          <legend style={{ fontWeight: 'bold', color: '#4285F4', fontSize: '1.1rem' }}>
            Subjects you can teach:
          </legend>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem 1rem',
              marginTop: '1rem'
            }}
          >
            {subjectOptions.map((subject) => (
              <label
                key={subject}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: '500',
                  color: '#333',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  minWidth: '120px',
                  padding: '0.25rem 0'
                }}
              >
                <input
                  type="checkbox"
                  checked={subjects.includes(subject)}
                  onChange={() => handleSubjectChange(subject)}
                  style={{
                    accentColor: '#4285F4',
                    marginRight: '0.5rem',
                    width: '18px',
                    height: '18px'
                  }}
                />
                {subject.charAt(0).toUpperCase() + subject.slice(1)}
              </label>
            ))}
          </div>
          <button
            style={{
              marginTop: '1rem',
              backgroundColor: '#4285F4',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontSize: '1rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? '0.7' : '1'
            }}
            onClick={handleSaveSubjects}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Subjects'}
          </button>        </fieldset>
      )}

      <StatusMessage
        message={statusMessage.message}
        type={statusMessage.type}
        isVisible={statusMessage.isVisible}
        onClose={statusMessage.hideMessage}
        position="top-right"
      />
    </div>
  );
};

export default MyInformation;
